import { IScheduleArray, IZoneArray } from '../../helpers/scheduleHelper';
import { IHeatingModalForm, ISelectedSchedule as ISelectedHeatingSchedule } from './heatingScheduleModal';
import { IHotWaterModalForm, ISelectedSchedule as ISelectedHotWaterSchedule } from './hotWaterScheduleModal';

export function heatingScheduleValidation(zoneSchedules: IZoneArray, defaultValues?: ISelectedHeatingSchedule) {
  const isEdit = Boolean(defaultValues);
  function scheduleOverlap(startTime, endTime, zoneStartTime, zoneEndTime, dayArray, zonesDays, isActiveZone) {
    if (isActiveZone) {
      return false;
    }
    let isTimeOverlap = false;
    // Check that the start & end times overlap
    if (
      (startTime >= zoneStartTime && startTime <= zoneEndTime) ||
      (endTime >= zoneStartTime && endTime <= zoneEndTime) ||
      (zoneStartTime >= startTime && zoneEndTime <= endTime)
    ) {
      isTimeOverlap = true;
    }
    return isTimeOverlap && dayArray.find((day) => zonesDays.includes(day));
  }
  function generateDateArray(dayObject: Omit<IHeatingModalForm, 'startTime' | 'endTime' | 'zones' | 'activeZones'>) {
    const days = [];
    for (const day in dayObject) {
      if (dayObject[day]) {
        days.push(day);
      }
    }
    return days;
  }
  // IMPROVE add typing for return type
  return function validate(data: IHeatingModalForm) {
    const errorArray = [];
    const { startTime, endTime, zones, activeZones, ...days } = data;
    const dateArray = generateDateArray(days);
    if (!dateArray.length) {
      errorArray.push({
        key: 'weekdays',
        message: 'Schedules must have at least 1 day assigned to them',
      });
    }

    if (startTime.length === 0) {
      errorArray.push({
        key: 'startTime',
        message: 'Start time must not be empty',
      });
    }

    if (endTime.length === 0) {
      errorArray.push({
        key: 'endTime',
        message: 'End time must not be empty',
      });
    }

    if (startTime.length && endTime.length && startTime >= endTime) {
      errorArray.push({
        key: 'startTime',
        message: 'Start time must occur before end time',
      });
      errorArray.push({
        key: 'endTime',
        message: 'End time must occur before start time',
      });
    }

    const zoneArray = [];
    let hasActiveZone = false;
    if (zones) {
      let index = 0;
      for (const zone of zones) {
        const key = `zones.${index}`;
        if (zone.active) {
          hasActiveZone = true;
          // const arrayKeys = keysByZones[zone.zone_id];
          const zonesArray = zoneSchedules[zone.zone_id];
          const activeZone = activeZones?.[zone.zone_id];
          if (
            zonesArray?.find((item, idx) =>
              scheduleOverlap(
                item.start,
                item.end,
                startTime,
                endTime,
                dateArray,
                zonesArray[idx]?.day,
                activeZone?.index === idx,
              ),
            )
          ) {
            errorArray.push({
              key: 'overlap',
              message: `This time overlaps with an existing schedule. This is for ${zone.description}`,
            });
          }
          const temp = zone.temperature;
          if ((typeof temp === 'string' && !temp.length) || (typeof temp === 'number' && !(temp > 0))) {
            errorArray.push({
              key: `${key}.temperature`,
              message: 'If active temperature cannot be empty',
            });
          }
          if (zone?.lower_bounds && +temp < zone?.lower_bounds) {
            errorArray.push({
              key: `${key}.temperature`,
              message: 'Temperature cannot be lower than the lower bounds',
            });
          }
          if (zone?.upper_bounds && +temp > zone?.upper_bounds) {
            errorArray.push({
              key: `${key}.temperature`,
              message: 'Temperature cannot be higher than the upper bounds',
            });
          }
          zoneArray.push({ ...zone });
        } else if (isEdit) {
          zoneArray.push({ ...zone });
        }
        index++;
      }
    }
    if (!hasActiveZone) {
      errorArray.push({
        key: 'overlap',
        message: 'You must select a zone for a schedule',
      });
    }

    if (!errorArray.length) {
      return {
        values: { ...data, zones: zoneArray, days: dateArray },
        errors: {},
      };
    }
    return {
      values: {},
      errors: errorArray.reduce(
        (prev, next) => ({
          ...prev,
          [next.key]: next.message,
        }),
        {},
      ),
    };
  };
}
export function hotWaterScheduleValidation(zoneSchedules: IZoneArray, defaultValues?: ISelectedHotWaterSchedule) {
  const isEdit = Boolean(defaultValues);
  function scheduleOverlap(startTime, zoneStartTime, dayArray, zonesDays) {
    let isTimeOverlap = false;
    // Check that the start & end times overlap
    if (startTime === zoneStartTime) {
      isTimeOverlap = true;
    }
    return isTimeOverlap && dayArray.find((day) => zonesDays.includes(day));
  }

  function generateDayArray(dayObject: Omit<IHotWaterModalForm, 'startTime'>) {
    const days = [];
    for (const day in dayObject) {
      if (dayObject[day]) {
        days.push(day);
      }
    }
    return days;
  }

  // IMPROVE add typing for return type
  return function validate(data: IHotWaterModalForm) {
    const errorArray = [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { startTime, ...days } = data;
    const dayArray = generateDayArray(days);
    const zonesArray = zoneSchedules.hot_water;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const findOverlaps = (filter = (_: IScheduleArray, __: number) => true) =>
      zonesArray
        ?.filter(filter)
        ?.find((item, idx) => scheduleOverlap(item.start, startTime, dayArray, zonesArray[idx]?.day));
    if (isEdit && findOverlaps((_, idx) => idx !== defaultValues.index)) {
      errorArray.push({
        key: 'overlap',
        message: 'This time overlaps with an existing schedule.',
      });
    } else if (!isEdit && findOverlaps()) {
      errorArray.push({
        key: 'overlap',
        message: 'This time overlaps with an existing schedule.',
      });
    }
    if (!dayArray.length) {
      errorArray.push({
        key: 'weekdays',
        message: 'Schedules must have at least 1 day assigned to them',
      });
    }

    if (startTime.length === 0) {
      errorArray.push({
        key: 'startTime',
        message: 'Start time must not be empty',
      });
    }

    if (!errorArray.length) {
      return { values: { ...data, days: dayArray }, errors: {} };
    }

    return {
      values: {},
      errors: errorArray.reduce(
        (prev, next) => ({
          ...prev,
          [next.key]: next.message,
        }),
        {},
      ),
    };
  };
}
